import React from 'react';
import ApplicationsIcon from 'assets/ApplicationsIcon';
import LinkedRecordOverviewDrawer from 'components/LinkedRecordOverviewDrawer';
import { OperationType } from 'components/RecordsListView/constants';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';
import { useAuth } from 'utilities/base-auth';
import useOperationProducts from 'screens/Applications&WorkOrders/Components/hooks/useOperationProducts';
import useOperationProperties from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import DrawerFooter from './DrawerFooter';
import useDrawerApplicationDetails from './hooks/useDrawerApplicationDetails';
import useDrawerPdfDownload from './hooks/useDrawerPdfDownload';

const HEADER_ICON_SIZE_PX = 16;

interface WorkOrderLinkedRecordDrawerProps {
  open: boolean;
  recordId: string | null;
  workOrderId?: string;
  onClose: () => void;
}

/** Records linked to a Work Order are Applications; the type is fixed here. */
const WorkOrderLinkedRecordDrawer = ({
  open,
  recordId,
  workOrderId,
  onClose
}: WorkOrderLinkedRecordDrawerProps) => {
  const { applicationDetails, isLoading, error } = useDrawerApplicationDetails(
    open ? recordId : null
  );

  const { products, isLoading: isLoadingProducts } = useOperationProducts(
    open ? recordId : null,
    'application'
  );

  const {
    propertiesData,
    isLoading: isLoadingProperties
  } = useOperationProperties(open ? recordId : null, 'application');

  // The details query holds `{}` with `isLoading` false before its first
  // response, so a missing id still counts as loading.
  const isLoadingOverview =
    !error &&
    (isLoading ||
      isLoadingProducts ||
      isLoadingProperties ||
      !applicationDetails?.id);

  const {
    user: { id: userId }
  } = useAuth();

  const { downloadPdf, isDownloading } = useDrawerPdfDownload({
    operation: applicationDetails,
    userId,
    isOpen: open,
    hasFailed: Boolean(error)
  });

  return (
    <LinkedRecordOverviewDrawer
      open={open}
      onClose={onClose}
      icon={
        <ApplicationsIcon
          width={HEADER_ICON_SIZE_PX}
          height={HEADER_ICON_SIZE_PX}
        />
      }
      typeLabel="APPLICATION"
      title={applicationDetails?.name ?? ''}
      cropSeasonIds={applicationDetails?.cropSeasonIds}
      type={OperationType.APPLICATION}
      details={(applicationDetails as unknown) as OperationDetails | undefined}
      products={products}
      propertiesData={propertiesData}
      tankInformation={applicationDetails?.tankInformation}
      isLoading={isLoadingOverview}
      hasError={Boolean(error)}
      footer={
        <DrawerFooter
          recordId={recordId}
          workOrderId={workOrderId}
          applicationCropSeasonIds={applicationDetails?.cropSeasonIds}
          onClose={onClose}
          onDownloadPdf={downloadPdf}
          isDownloadingPdf={isDownloading}
        />
      }
    />
  );
};

export default WorkOrderLinkedRecordDrawer;
